/**
 * ThemeSwatch: a small card for a theme gallery — a mini static render of the
 * scene, the level number and the theme's name in the current language.
 *
 * Renders as a <button> when `onSelect` is given, otherwise as a plain card.
 * Thumbnails are painted through a shared queue that spends at most ~8 ms per
 * animation frame, so a gallery of 24 swatches never freezes the page.
 */
import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import type { Locale, ThemePack } from '@dropstack/core';
import { drawThemePreview } from './preview.ts';

export interface ThemeSwatchProps {
  theme: ThemePack;
  locale: Locale;
  /** Thumbnail width in CSS px (default 144). The thumbnail is 4:5 portrait, like the game screen. */
  width?: number;
  /** Highlighted as the current choice. */
  selected?: boolean;
  /** Not reached yet: dimmed, with a lock badge. Still focusable and selectable, so a gallery can preview it. */
  locked?: boolean;
  /** Accessible text for the lock badge, in the current language (e.g. "Locked" / "Gesluit"). */
  lockedLabel?: string;
  onSelect?: (theme: ThemePack) => void;
  className?: string;
}

// ───────────── paint queue ─────────────

const queue: { run: () => void; cancelled: boolean }[] = [];
let pumping = false;

function pump(): void {
  const start = performance.now();
  while (queue.length > 0 && performance.now() - start < 8) {
    const job = queue.shift()!;
    if (!job.cancelled) job.run();
  }
  if (queue.length > 0) requestAnimationFrame(pump);
  else pumping = false;
}

/** Run `run` soon, sharing frame time with other thumbnails. Returns a cancel function. */
function schedulePaint(run: () => void): () => void {
  const job = { run, cancelled: false };
  queue.push(job);
  if (!pumping) {
    pumping = true;
    requestAnimationFrame(pump);
  }
  return () => {
    job.cancelled = true;
  };
}

// ───────────── component ─────────────

function LockBadge({ color, background, label }: { color: string; background: string; label?: string }) {
  const a11y = label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true };
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" {...a11y} style={{ position: 'absolute', right: 6, top: 6 }}>
      {label && <title>{label}</title>}
      <circle cx="12" cy="12" r="11" fill={background} />
      <rect x="7" y="11" width="10" height="8" rx="2" fill={color} />
      <path d="M9 11V8.5a3 3 0 0 1 6 0V11" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export function ThemeSwatch({ theme, locale, width = 144, selected = false, locked = false, lockedLabel, onSelect, className }: ThemeSwatchProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const height = Math.round(width * 1.25);
  const p = theme.palette;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    return schedulePaint(() => drawThemePreview(canvas, theme, { width, height }));
  }, [theme, width, height]);

  const card: CSSProperties = {
    display: 'inline-flex',
    flexDirection: 'column',
    gap: 6,
    width: width + 12,
    padding: 6,
    margin: 0,
    border: `3px solid ${selected ? p.accent : 'transparent'}`,
    borderRadius: 18,
    background: p.uiPanel,
    color: p.uiText,
    font: 'inherit',
    textAlign: 'center',
    cursor: onSelect ? 'pointer' : 'default',
    boxShadow: selected ? `0 0 0 3px ${p.uiPanel}, 0 6px 18px rgba(0, 0, 0, 0.12)` : '0 3px 10px rgba(0, 0, 0, 0.08)',
    opacity: locked ? 0.85 : 1,
  };
  const content: ReactNode = (
    <>
      <span style={{ position: 'relative', display: 'block', width, height }}>
        <canvas
          ref={canvasRef}
          aria-hidden="true"
          style={{ display: 'block', width, height, borderRadius: 12, filter: locked ? 'grayscale(0.8) brightness(1.05)' : undefined }}
        />
        <span
          style={{
            position: 'absolute',
            left: 6,
            top: 6,
            minWidth: 26,
            padding: '2px 7px',
            borderRadius: 999,
            background: p.buttonBg,
            color: p.buttonText,
            fontWeight: 700,
            fontSize: 14,
            lineHeight: '20px',
          }}
        >
          {theme.level}
        </span>
        {locked && <LockBadge color={p.uiText} background={p.uiPanel} label={lockedLabel} />}
      </span>
      <span style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.2, padding: '0 2px 2px' }}>{theme.name[locale]}</span>
    </>
  );

  if (!onSelect) {
    return (
      <div className={className} style={card} title={theme.tagline[locale]} data-theme-id={theme.id}>
        {content}
      </div>
    );
  }
  return (
    <button
      type="button"
      className={className}
      style={card}
      title={theme.tagline[locale]}
      data-theme-id={theme.id}
      aria-pressed={selected}
      onClick={() => onSelect(theme)}
    >
      {content}
    </button>
  );
}
